import lottie from "lottie-web";
import { AI_ASSISTANT_SUPPORTED_WIDGETS } from "@/constants";
import type { elementBase } from "@/elementBase/elementBase";
import { PATH_TO_STATIC } from "@/elements/aiAnswer/constants";
import type { shapeWidget } from "@/elements/shape";
import type { stickerWidget } from "@/elements/sticker";
import type { tableWidget } from "@/elements/table/table";
import type { textWidget } from "@/elements/text/text";
import type { Group } from "@/group";
import { MAX_NUMBER_OF_WIDGETS_FOR_AI_PROCESS, MIN_NUMBER_OF_SYMBOLS } from "./constants";

function getTableCeilsQuantity(table: tableWidget) {
    let result = 0;
    for (const row of table.rows) {
        result += row.cells.length;
    }

    return result;
}

function checkIsWidgetsQuantityExceeded(groups: Group[]) {
    if (groups.length > MAX_NUMBER_OF_WIDGETS_FOR_AI_PROCESS) {
        return true;
    }

    let widgetsQuantity = 0;

    for (const group of groups) {
        if (group.isItemWrapper()) {
            if (group.onlyChild()?.constructorName === "table") {
                widgetsQuantity += getTableCeilsQuantity(group.onlyChild() as tableWidget);

                continue;
            }
            widgetsQuantity++;

            continue;
        }

        group.children.forEach((child) => {
            if ("rows" in child) {
                widgetsQuantity += getTableCeilsQuantity(child as tableWidget);

                return;
            }

            widgetsQuantity++;
        });
    }

    if (widgetsQuantity > MAX_NUMBER_OF_WIDGETS_FOR_AI_PROCESS) {
        return true;
    }

    return false;
}

function isWidgetWithEnoughText(widget: elementBase): boolean {
    switch (widget.constructorName) {
        case "table": {
            let result = true;
            outer: for (const row of (widget as tableWidget).rows) {
                for (const cell of row.cells) {
                    if (cell.textPart.getText().symbolsCount === 0) {
                        result = false;
                        break outer;
                    }
                }
            }

            return result;
        }

        case "sticker":
        case "shape":
            return (
                (widget as stickerWidget | shapeWidget).textPart.symbolsCount >
                MIN_NUMBER_OF_SYMBOLS
            );
        case "text":
            return (widget as textWidget).symbolsCount > MIN_NUMBER_OF_SYMBOLS;
        default:
            return false;
    }
}

function processSelectedWidgets(groups: Group[], mode: string) {
    //Для включения механизма undo/redo для ai фич нужно расскоментировать строки

    const result: {
        widgetsState: {
            [UID: string]: unknown;
        };
        widgetUIDs: string[];
    } = {
        widgetUIDs: [],
        widgetsState: {},
    };

    /* const fixType = mode === "create" ? "full" : "bold"; */

    for (const group of groups) {
        if (group.isItemWrapper()) {
            if (
                AI_ASSISTANT_SUPPORTED_WIDGETS.includes(
                    (group.children[0] as elementBase).constructorName,
                ) &&
                isWidgetWithEnoughText(group.children[0] as elementBase)
            ) {
                /* result.widgetsState[group.children[0].UID] = group.children[0].getState(fixType); */
                result.widgetUIDs.push(group.children[0].UID);
            }

            continue;
        }

        (group.children as Group[]).forEach((child) => {
            if (child.isItemWrapper()) {
                if (
                    AI_ASSISTANT_SUPPORTED_WIDGETS.includes(
                        (child.children[0] as elementBase).constructorName,
                    ) &&
                    isWidgetWithEnoughText(child.children[0] as elementBase)
                ) {
                    /* result.widgetsState[child.children[0].UID] = child.children[0].getState(fixType); */
                    result.widgetUIDs.push(child.children[0].UID);
                }

                return;
            }

            (child.children as Group[]).forEach((innerChild) => {
                if (
                    AI_ASSISTANT_SUPPORTED_WIDGETS.includes(
                        (innerChild.children[0] as elementBase).constructorName,
                    ) &&
                    isWidgetWithEnoughText(innerChild.children[0] as elementBase)
                ) {
                    /* result.widgetsState[innerChild.children[0].UID] =
                        innerChild.children[0].getState(fixType); */
                    result.widgetUIDs.push(innerChild.children[0].UID);
                }
            });
        });
    }

    return result;
}

function checkIsAiGeneratedTextInWidget(widget: elementBase): boolean {
    switch (widget.constructorName) {
        case "table":
            return (widget as tableWidget).aiGenerated;

        case "sticker":
        case "shape":
            return (widget as stickerWidget | shapeWidget).textPart.aiGenerated;
        case "text":
            return (widget as textWidget).aiGenerated;
        default:
            return false;
    }
}

function createButtonAnimation(container: HTMLDivElement, host: string) {
    const animation = lottie.loadAnimation({
        container,
        renderer: "svg",
        loop: true,
        autoplay: true,
        path: `${host}${PATH_TO_STATIC}button-animation.json`,
    });

    container.style.display = "block";

    return animation;
}

export {
    checkIsWidgetsQuantityExceeded,
    processSelectedWidgets,
    createButtonAnimation,
    checkIsAiGeneratedTextInWidget,
};
